/**
 * DeviceSelector Modal
 * Modal for selecting Spotify Connect playback device
 */

import { useState, useEffect } from 'react';
import { Monitor, Smartphone, Tv, Speaker, RefreshCw, X, Volume2 } from 'lucide-react';
import { useDevices, useSelectDevice } from '@/api/hooks';
import { Button } from '@/components/shared/Button';
import { EmptyState } from '@/components/shared/EmptyState';
import { usePlayerUIStore } from '@/stores/player';

export interface DeviceSelectorProps {
  isOpen: boolean;
  onClose: () => void;
}

export function DeviceSelector({ isOpen, onClose }: DeviceSelectorProps) {
  // Only fetch/poll devices while the modal is actually open - it's mounted
  // permanently by PlayerBar with visibility controlled via `isOpen`.
  const { data, isLoading, error, refetch } = useDevices({ enabled: isOpen });
  const selectDevice = useSelectDevice();
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
  const executePendingPlayAction = usePlayerUIStore((s) => s.executePendingPlayAction);

  // Poll for devices while modal is open
  useEffect(() => {
    if (!isOpen) return;

    const interval = setInterval(() => {
      refetch();
    }, 3000); // Poll every 3 seconds

    return () => clearInterval(interval);
  }, [isOpen, refetch]);

  const handleSelectDevice = async (deviceId: string) => {
    if (!deviceId) return;

    setSelectedDeviceId(deviceId);
    try {
      await selectDevice.mutateAsync({ deviceId, forcePlay: false });

      // Wait a bit for Spotify API to update before closing
      // This ensures the UI shows the updated device state
      await new Promise(resolve => setTimeout(resolve, 300));

      onClose(); // Close modal on success

      // Execute pending play action if one exists
      // This allows automatic playback after device selection
      executePendingPlayAction();
    } catch (err) {
      // Error will be shown via toast or error display
      console.error('Failed to select device:', err);
      setSelectedDeviceId(null);
    }
  };

  const handleRefresh = () => {
    refetch();
  };

  const getDeviceIcon = (type: string) => {
    const deviceType = type.toLowerCase();

    if (deviceType.includes('computer')) {
      return Monitor;
    } else if (deviceType.includes('smartphone') || deviceType.includes('phone')) {
      return Smartphone;
    } else if (deviceType.includes('tv') || deviceType.includes('cast')) {
      return Tv;
    } else {
      return Speaker;
    }
  };

  if (!isOpen) return null;

  const devices = data?.devices || [];
  const hasDevices = devices.length > 0;

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="device-selector-title"
    >
      <div
        className="bg-zinc-900 border border-zinc-800 rounded-lg shadow-2xl max-w-md w-full mx-4 animate-in fade-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-600/20 rounded-full flex items-center justify-center">
              <Speaker className="w-5 h-5 text-green-500" />
            </div>
            <h2 id="device-selector-title" className="text-xl font-semibold text-white">
              Select Playback Device
            </h2>
          </div>

          <div className="flex items-center gap-2">
            {/* Refresh Button */}
            <button
              onClick={handleRefresh}
              disabled={isLoading}
              className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Refresh devices"
              title="Refresh devices"
            >
              <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
            </button>

            {/* Close Button */}
            <button
              onClick={onClose}
              className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg transition-colors"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {/* Loading State */}
          {isLoading && (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <div
                  key={i}
                  className="h-16 bg-zinc-800/50 rounded-lg animate-pulse"
                />
              ))}
            </div>
          )}

          {/* Error State */}
          {error && !isLoading && (
            <div className="space-y-4">
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                <p className="text-red-400 text-sm">
                  Failed to load devices. Please try again.
                </p>
              </div>
              <Button
                onClick={handleRefresh}
                variant="secondary"
                className="w-full"
              >
                Retry
              </Button>
            </div>
          )}

          {/* Empty State */}
          {!isLoading && !error && !hasDevices && (
            <EmptyState
              icon={Speaker}
              title="No Devices Found"
              message="Open Spotify on another device to see it here. Make sure you're logged in with the same account."
              className="py-8"
            />
          )}

          {/* Device List */}
          {!isLoading && !error && hasDevices && (
            <div className="space-y-2">
              {devices.map((device) => {
                const DeviceIcon = getDeviceIcon(device.type);
                const isSelecting = selectedDeviceId === device.deviceId;
                const isActiveDevice = device.isActive;

                return (
                  <button
                    key={device.deviceId || device.name}
                    onClick={() => device.deviceId && handleSelectDevice(device.deviceId)}
                    disabled={isSelecting || !device.deviceId}
                    className={`
                      w-full flex items-center gap-4 p-4 rounded-lg
                      transition-all duration-200
                      ${isActiveDevice
                        ? 'bg-green-600/20 border-2 border-green-600/50'
                        : 'bg-zinc-800/50 border-2 border-transparent hover:bg-zinc-800 hover:border-zinc-700'
                      }
                      ${isSelecting ? 'opacity-50 cursor-wait' : ''}
                      ${!device.deviceId ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                    `}
                    aria-label={`Select ${device.name}${isActiveDevice ? ' (currently active)' : ''}`}
                  >
                    {/* Device Icon */}
                    <div className={`
                      w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0
                      ${isActiveDevice ? 'bg-green-600/30' : 'bg-zinc-700/50'}
                    `}>
                      <DeviceIcon className={`
                        w-6 h-6
                        ${isActiveDevice ? 'text-green-400' : 'text-zinc-400'}
                      `} />
                    </div>

                    {/* Device Info */}
                    <div className="flex-1 text-left min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`
                          font-medium truncate
                          ${isActiveDevice ? 'text-green-400' : 'text-white'}
                        `}>
                          {device.name}
                        </p>
                        {isActiveDevice && (
                          <span className="flex-shrink-0 w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                        )}
                      </div>

                      <div className="flex items-center gap-3 mt-1">
                        <p className="text-sm text-zinc-500 capitalize">
                          {device.type}
                        </p>

                        {/* Volume */}
                        {device.volumePercent !== null && device.volumePercent !== undefined && (
                          <div className="flex items-center gap-1.5 text-xs text-zinc-500">
                            <Volume2 className="w-3.5 h-3.5" />
                            <span>{device.volumePercent}%</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Active Indicator Badge */}
                    {isActiveDevice && (
                      <div className="flex-shrink-0 px-2.5 py-1 bg-green-600/30 rounded-full">
                        <span className="text-xs font-medium text-green-400">
                          Active
                        </span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer hint */}
        {!isLoading && !error && hasDevices && (
          <div className="px-6 py-3 border-t border-zinc-800 bg-zinc-900/50">
            <p className="text-xs text-zinc-500 text-center">
              Click a device to transfer playback
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
